<template>
  <div class="user-services-page">
    <UserAppChrome back-mode="history" />

    <main class="services-shell">
      <header class="services-hero">
        <div class="services-hero__copy">
          <span class="services-eyebrow">Percorsi e servizi</span>
          <h1>Scegli il tuo servizio.</h1>
          <p>
            Confronta supporto, durata e prezzo. Il tuo account Fusco Fit è già collegato.
          </p>
        </div>
        <div class="services-hero__mark" aria-hidden="true">
          <span>FITNESS</span>
          <strong>COACHING</strong>
        </div>
      </header>

      <nav class="services-nav" aria-label="Navigazione servizi">
        <button type="button" @click="scrollToSection('coaching-plans')">
          <span class="material-icons" aria-hidden="true">route</span>
          Coaching online
        </button>
        <button type="button" @click="scrollToSection('personal-training')">
          <span class="material-icons" aria-hidden="true">fitness_center</span>
          Pacchetti PT
        </button>
      </nav>

      <section id="coaching-plans" class="services-section services-section--coaching" aria-labelledby="coaching-title">
        <div class="section-heading">
          <span>01 · Coaching online</span>
          <h2 id="coaching-title">Scegli supporto e durata</h2>
          <p>Tutti i percorsi includono allenamento e alimentazione personalizzati.</p>
        </div>

        <div class="level-switch level-switch--services" role="radiogroup" aria-label="Livello di supporto">
          <button
            v-for="level in coachingLevels"
            :key="level.id"
            type="button"
            :class="[{ active: selectedLevel === level.id }, `level-switch__option--${level.id}`]"
            role="radio"
            :aria-checked="selectedLevel === level.id"
            @click="selectLevel(level.id)"
          >
            <span class="level-switch__check material-icons" aria-hidden="true">
              {{ selectedLevel === level.id ? 'check_circle' : 'radio_button_unchecked' }}
            </span>
            <span class="level-switch__copy">
              <strong>{{ level.name }}</strong>
              <small>{{ level.shortLabel }}</small>
            </span>
            <span class="level-switch__price">da {{ level.monthlyFrom }}</span>
          </button>
        </div>

        <details :class="['level-summary', `level-summary--${selectedLevel}`]">
          <summary>
            <div class="level-summary__copy">
              <span>{{ selectedLevelDetails.name }} · {{ selectedLevelDetails.monthlyFrom }}</span>
              <h3>{{ selectedLevelDetails.title }}</h3>
            </div>
            <span class="level-summary__toggle">
              Cosa include
              <span class="material-icons" aria-hidden="true">expand_more</span>
            </span>
          </summary>
          <div class="level-summary__body">
            <p>{{ selectedLevelDetails.description }}</p>
            <ul>
              <li v-for="feature in selectedLevelDetails.features" :key="feature">
                <span class="material-icons" aria-hidden="true">check</span>
                {{ feature }}
              </li>
            </ul>
          </div>
        </details>

        <div class="plans-heading" id="plans-title">
          <div>
            <span>Pacchetti {{ selectedLevelLabel }}</span>
            <strong>Scegli la durata</strong>
          </div>
          <span>{{ selectedCoachingPlans.length }} opzioni</span>
        </div>

        <div class="plan-grid" aria-labelledby="plans-title">
          <article v-for="plan in selectedCoachingPlans" :key="plan.key" class="plan-card">
            <div class="plan-card__heading">
              <span>{{ programmeLabel(plan.programmes) }}</span>
              <strong>{{ plan.product.duration }}</strong>
            </div>
            <div class="plan-card__price">
              <strong>{{ paymentPrimaryLabel(plan.product) }}</strong>
              <span>{{ paymentSecondaryLabel(plan.product) }}</span>
            </div>
            <button
              type="button"
              class="plan-card__purchase"
              :aria-label="`Acquista il pacchetto da ${programmeLabel(plan.programmes)}`"
              @click="openCheckout(plan.key)"
            >
              <span class="material-icons" aria-hidden="true">add_shopping_cart</span>
            </button>
            <details class="plan-card__details">
              <summary>
                <span>Cosa include</span>
                <span class="material-icons" aria-hidden="true">expand_more</span>
              </summary>
              <ul>
                <li v-for="item in plan.inclusions" :key="item.label">
                  <strong>{{ item.value }}</strong>
                  <span>{{ item.label }}</span>
                </li>
              </ul>
            </details>
          </article>
        </div>
      </section>

      <section id="maintenance" class="maintenance-section" aria-labelledby="maintenance-title">
        <div class="maintenance-section__intro">
          <span class="maintenance-section__icon material-icons" aria-hidden="true">verified_user</span>
          <div>
            <span class="maintenance-section__eyebrow">Servizio riservato</span>
            <h2 id="maintenance-title">Mantenimento</h2>
            <p>
              Per continuare il percorso con Fusco Fit dopo la fase principale, senza perdere il lavoro svolto.
            </p>
          </div>
        </div>
        <div class="maintenance-section__rules">
          <span class="maintenance-section__status">
            <span class="material-icons" aria-hidden="true">lock</span>
            Attivazione tramite staff
          </span>
          <ul>
            <li>Percorso Fusco Fit attualmente attivo</li>
            <li>Oppure concluso da non oltre 3 mesi</li>
            <li>Verifica e autorizzazione del team</li>
          </ul>
          <a :href="maintenanceWhatsappUrl" target="_blank" rel="noopener noreferrer">
            Richiedi la verifica
            <span class="material-icons" aria-hidden="true">arrow_forward</span>
          </a>
        </div>
      </section>

      <section id="personal-training" class="services-section" aria-labelledby="pt-title">
        <div class="section-heading">
          <span>02 · Personal Training</span>
          <h2 id="pt-title">In studio o online.</h2>
          <p>Sedute individuali con validità definita, acquistabili anche separatamente dal coaching.</p>
        </div>

        <div class="pt-groups">
          <section v-for="group in ptGroups" :key="group.id" class="pt-group">
            <div class="pt-group__heading">
              <span class="material-icons" aria-hidden="true">{{ group.icon }}</span>
              <div>
                <span>Personal Training</span>
                <h3>{{ group.title }}</h3>
              </div>
            </div>
            <div class="pt-plan-list">
              <article v-for="plan in group.plans" :key="plan.key" class="pt-plan">
                <div class="pt-plan__heading">
                  <span>{{ plan.sessions }} sedute</span>
                  <strong>{{ plan.product.duration }}</strong>
                </div>
                <div class="pt-plan__price">
                  <strong>{{ formatPrice(plan.product.price) }}</strong>
                  <span>Pagamento unico</span>
                </div>
                <button
                  type="button"
                  class="pt-plan__purchase"
                  :aria-label="`Acquista ${plan.sessions} sedute ${group.title}`"
                  @click="openCheckout(plan.key)"
                >
                  <span class="material-icons" aria-hidden="true">add_shopping_cart</span>
                </button>
              </article>
            </div>
          </section>
        </div>
      </section>

      <section class="services-help" aria-labelledby="help-title">
        <div>
          <span class="services-eyebrow">Hai bisogno di una soluzione diversa?</span>
          <h2 id="help-title">Costruiamola insieme.</h2>
          <p>
            Se ti serve solo allenamento o solo alimentazione, il team può costruire una soluzione adatta alla tua situazione.
          </p>
        </div>
        <a :href="customServiceWhatsappUrl" target="_blank" rel="noopener noreferrer">
          <span class="material-icons" aria-hidden="true">chat</span>
          Contatta il team
        </a>
      </section>
    </main>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import UserAppChrome from './UserAppChrome.vue';
import { PUBLIC_CHECKOUT_PRODUCTS } from '@/lib/publicCheckoutProducts';

const COACHING_LEVELS = Object.freeze([
  {
    id: 'plus',
    name: 'Plus',
    shortLabel: 'Assistenza con i trainer',
    monthlyFrom: '95 €/mese',
    title: 'Supporto tecnico costante',
    description: 'La scelta equilibrata per essere seguito dal trainer durante ogni cambio programma.',
    features: [
      'Allenamento e alimentazione personalizzati',
      'Videocall con il trainer a ogni cambio scheda',
      'Coaching Day e incontri presso gli studi',
      'Supporto digitale e monitoraggio',
    ],
  },
  {
    id: 'top',
    name: 'TOP',
    badge: 'Supporto completo',
    shortLabel: 'Assistenza con Giampaolo Fusco',
    monthlyFrom: '120 €/mese',
    title: 'Diretto con Giampaolo',
    description: 'Il livello più completo, con assistenza personale e contatto diretto.',
    features: [
      'Tutto ciò che è incluso nel Plus',
      'Contatto diretto con Giampaolo Fusco',
      'Assistenza personale tramite WhatsApp',
      'Priorità nel supporto e Coaching Day',
    ],
  },
]);

const COACHING_PLANS = Object.freeze({
  plus: [
    { key: 'fc1-plus', programmes: 1 },
    { key: 'fc4-plus', programmes: 4 },
    { key: 'fc6-plus', programmes: 6 },
  ],
  top: [
    { key: 'fc1-top', programmes: 1 },
    { key: 'fc2-top', programmes: 2 },
    { key: 'fc3-top', programmes: 3 },
    { key: 'fc4-top', programmes: 4 },
    { key: 'fc6-top', programmes: 6 },
    { key: 'fc10-top', programmes: 10 },
  ],
});

const NUTRITIONAL_CONSULTATIONS = Object.freeze({
  1: 1,
  2: 1,
  3: 1,
  4: 2,
  6: 3,
  10: 5,
});

const createPlanInclusions = (programmes, level) => {
  const nutritionalConsultations = level === 'top'
    ? (NUTRITIONAL_CONSULTATIONS[programmes] || 1)
    : 1;

  return [
    {
      value: programmes,
      label: programmes === 1 ? 'programma di allenamento' : 'programmi di allenamento',
    },
    {
      value: programmes,
      label: programmes === 1 ? 'piano di alimentazione' : 'piani di alimentazione',
    },
    {
      value: programmes,
      label: 'video call con il trainer',
    },
    {
      value: nutritionalConsultations,
      label: nutritionalConsultations === 1 ? 'consulenza nutrizionale' : 'consulenze nutrizionali',
    },
  ];
};

const PT_GROUPS = Object.freeze([
  {
    id: 'studio',
    title: 'In studio',
    icon: 'fitness_center',
    plans: [
      { key: 'pt10', sessions: 10 },
      { key: 'pt20', sessions: 20 },
    ],
  },
  {
    id: 'online',
    title: 'Online',
    icon: 'videocam',
    plans: [
      { key: 'ptonline10', sessions: 10 },
      { key: 'ptonline20', sessions: 20 },
    ],
  },
]);

const resolveProduct = key => PUBLIC_CHECKOUT_PRODUCTS[key] || null;

export default {
  name: 'UserServices',
  components: { UserAppChrome },
  setup() {
    const router = useRouter();
    const selectedLevel = ref('plus');

    const coachingLevels = COACHING_LEVELS;
    const selectedLevelDetails = computed(() => (
      coachingLevels.find(level => level.id === selectedLevel.value) || coachingLevels[0]
    ));
    const selectedLevelLabel = computed(() => (
      selectedLevelDetails.value.name
    ));
    const selectedCoachingPlans = computed(() => (
      (COACHING_PLANS[selectedLevel.value] || [])
        .map(plan => ({
          ...plan,
          product: resolveProduct(plan.key),
          inclusions: createPlanInclusions(plan.programmes, selectedLevel.value),
        }))
        .filter(plan => plan.product)
    ));
    const ptGroups = PT_GROUPS.map(group => ({
      ...group,
      plans: group.plans
        .map(plan => ({ ...plan, product: resolveProduct(plan.key) }))
        .filter(plan => plan.product),
    }));

    const customServiceWhatsappUrl = `https://wa.me/393279136921?text=${encodeURIComponent('Ciao, sono già cliente Fusco Fit e vorrei informazioni su un nuovo servizio o su un percorso personalizzato.')}`;
    const maintenanceWhatsappUrl = `https://wa.me/393279136921?text=${encodeURIComponent('Ciao, vorrei verificare se posso attivare il servizio di Mantenimento Fusco Fit.')}`;

    onMounted(async () => {
      await nextTick();
      const resetPageScroll = () => {
        const appContainers = document.querySelectorAll('#app');
        appContainers[appContainers.length - 1]?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      };
      resetPageScroll();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          resetPageScroll();
        });
      });
    });

    const formatPrice = value => new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);

    const paymentTerms = product => {
      const installments = Number(product?.defaultAlmaInstallments);
      const price = Number(product?.price);
      if (!Number.isInteger(installments) || installments < 2 || !Number.isFinite(price)) {
        return null;
      }
      return {
        count: installments,
        installment: price / installments,
        total: price,
      };
    };

    const paymentPrimaryLabel = product => {
      const terms = paymentTerms(product);
      return terms
        ? `${formatPrice(terms.installment)} al mese per ${terms.count} rate`
        : formatPrice(product?.price);
    };

    const paymentSecondaryLabel = product => {
      const terms = paymentTerms(product);
      return terms ? `Totale ${formatPrice(terms.total)}` : 'Pagamento unico';
    };

    const programmeLabel = count => `${count} ${count === 1 ? 'programma' : 'programmi'}`;

    const selectLevel = level => {
      selectedLevel.value = level;
    };

    const scrollToSection = id => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const openCheckout = sku => {
      router.push({
        name: 'PublicCheckout',
        query: { sku, source: 'app-servizi' },
      });
    };

    return {
      coachingLevels,
      customServiceWhatsappUrl,
      formatPrice,
      maintenanceWhatsappUrl,
      openCheckout,
      paymentPrimaryLabel,
      paymentSecondaryLabel,
      programmeLabel,
      ptGroups,
      scrollToSection,
      selectLevel,
      selectedCoachingPlans,
      selectedLevel,
      selectedLevelDetails,
      selectedLevelLabel,
    };
  },
};
</script>

<style scoped>
.user-services-page {
  --ff-bg: #f5f4f0;
  --ff-surface: rgba(255, 255, 255, 0.72);
  --ff-white: #ffffff;
  --ff-black: #111110;
  --ff-gray-2: #4a4a48;
  --ff-gray-3: #8a8a87;
  --ff-border: rgba(17, 17, 16, 0.1);
  --ff-red: #c8102e;
  min-height: 100vh;
  background:
    radial-gradient(circle at 90% 7%, rgba(200, 16, 46, 0.09), transparent 26rem),
    var(--ff-bg);
  color: var(--ff-black);
  font-family: "DM Sans", sans-serif;
  font-size: var(--ff-type-body);
  overflow-x: hidden;
}

.services-shell {
  box-sizing: border-box;
  width: min(100%, 1440px);
  margin: 0 auto;
  padding: clamp(28px, 5vw, 72px) clamp(20px, 5vw, 72px) 140px;
}

.services-hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 24px;
  min-height: 250px;
  padding: clamp(28px, 4vw, 46px);
  border-radius: 24px;
  background:
    radial-gradient(circle at 90% 10%, rgba(228, 55, 83, 0.38), transparent 35%),
    linear-gradient(145deg, #1c1c1b, #0e0e0d);
  color: #fff;
  box-shadow: 0 28px 70px rgba(17, 17, 16, 0.2);
  overflow: hidden;
}

.services-hero::after {
  content: "";
  position: absolute;
  right: -110px;
  bottom: -190px;
  width: 380px;
  height: 380px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 50%;
  box-shadow: 0 0 0 48px rgba(255, 255, 255, 0.025), 0 0 0 96px rgba(255, 255, 255, 0.018);
}

.services-hero__copy,
.services-hero__mark {
  position: relative;
  z-index: 1;
}

.services-eyebrow,
.section-heading > span,
.section-heading > div > span {
  display: block;
  margin-bottom: 10px;
  color: #dc2848;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.services-hero .services-eyebrow {
  color: #ff8aa0;
}

.services-hero h1 {
  max-width: 760px;
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: clamp(2.15rem, 5vw, 4rem);
  font-weight: 900;
  letter-spacing: -0.06em;
  line-height: 0.92;
  text-transform: uppercase;
}

.services-hero__copy > p {
  max-width: 620px;
  margin: 16px 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: clamp(0.95rem, 1.5vw, 1.08rem);
  line-height: 1.55;
}

.services-hero__mark {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: rgba(255, 255, 255, 0.18);
  font-family: "Montserrat", sans-serif;
  font-size: clamp(0.85rem, 2vw, 1.1rem);
  font-weight: 700;
  letter-spacing: 0.18em;
}

.services-hero__mark strong {
  font-size: clamp(1.8rem, 4vw, 3.5rem);
  letter-spacing: -0.04em;
}

.services-section {
  padding-top: clamp(46px, 6vw, 72px);
  scroll-margin-top: 80px;
}

.section-heading {
  max-width: 760px;
  margin-bottom: 18px;
}

.section-heading--row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  max-width: none;
}

.section-heading h2,
.services-help h2 {
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: clamp(1.75rem, 4vw, 3rem);
  font-weight: 900;
  letter-spacing: -0.055em;
  line-height: 0.98;
  text-transform: uppercase;
}

.section-heading h2 {
  color: #111110;
  text-align: left;
}

.services-help h2 {
  color: #fff;
  text-align: left;
}

.section-heading p,
.services-help p {
  margin: 9px 0 0;
  color: #585854;
  font-size: var(--ff-type-body-small);
  line-height: 1.55;
}

.level-switch {
  display: inline-flex;
  padding: 4px;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.68);
}

.level-switch button {
  min-width: 78px;
  padding: 9px 14px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #656560;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 800;
  cursor: pointer;
}

.level-switch button.active {
  background: #111110;
  color: #fff;
}

.services-nav {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 14px 0 0;
  padding: 8px;
  border: 1px solid var(--ff-border);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 12px 34px rgba(17, 17, 16, 0.06);
}

.services-nav button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  min-height: 44px;
  padding: 9px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--ff-gray-2);
  font: inherit;
  font-size: var(--ff-type-label);
  font-weight: 800;
  cursor: pointer;
}

.services-nav button:hover,
.services-nav button:focus-visible {
  background: rgba(200, 16, 46, 0.07);
  color: var(--ff-red);
  outline: none;
}

.services-nav .material-icons {
  color: var(--ff-red);
  font-size: 19px;
}

.services-section--coaching {
  padding-top: clamp(38px, 5vw, 58px);
}

.level-switch--services {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 7px;
  border-radius: 20px;
}

.level-switch--services button {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 11px;
  min-width: 0;
  min-height: 66px;
  padding: 10px 14px;
  border-radius: 15px;
  text-align: left;
}

.level-switch--services button.active {
  background: var(--ff-red);
  box-shadow: 0 10px 28px rgba(200, 16, 46, 0.2);
}

.level-switch--services .level-switch__option--top.active {
  background: #171612;
  box-shadow: 0 10px 28px rgba(17, 17, 16, 0.2);
}

.level-switch__check {
  color: var(--ff-red);
  font-size: 21px;
}

.level-switch--services button.active .level-switch__check {
  color: #fff;
}

.level-switch__copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.level-switch__copy strong {
  font-family: "Montserrat", sans-serif;
  font-size: var(--ff-type-title);
  letter-spacing: -0.035em;
  line-height: 1.1;
  text-transform: uppercase;
}

.level-switch__copy small {
  margin-top: 3px;
  color: #777772;
  font-size: var(--ff-type-label-small);
  font-weight: 600;
  line-height: 1.18;
  white-space: normal;
}

.level-switch--services button.active .level-switch__copy small,
.level-switch--services button.active .level-switch__price {
  color: rgba(255, 255, 255, 0.75);
}

.level-switch__price {
  color: #6d6d68;
  font-size: var(--ff-type-label);
  font-weight: 800;
  white-space: nowrap;
}

.level-summary {
  margin-top: 10px;
  border: 1px solid rgba(200, 16, 46, 0.16);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 12px 34px rgba(17, 17, 16, 0.055);
  overflow: hidden;
}

.level-summary--top {
  border-color: rgba(218, 171, 54, 0.34);
  background:
    radial-gradient(circle at 100% 0, rgba(218, 171, 54, 0.18), transparent 42%),
    #171612;
  color: #fff;
}

.level-summary summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 52px;
  padding: 12px 16px;
  cursor: pointer;
  list-style: none;
}

.level-summary summary::-webkit-details-marker {
  display: none;
}

.level-summary__toggle {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  color: #60605c;
  font-size: var(--ff-type-label-small);
  font-weight: 800;
  text-transform: uppercase;
}

.level-summary--top .level-summary__toggle {
  color: rgba(255, 255, 255, 0.72);
}

.level-summary--top .level-summary__toggle .material-icons {
  color: #fff !important;
}

.level-summary__toggle .material-icons {
  font-size: 19px;
  transition: transform 0.2s ease;
}

.level-summary[open] .level-summary__toggle .material-icons {
  transform: rotate(180deg);
}

.level-summary__body {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  align-items: start;
  gap: 18px;
  padding: 15px 16px 17px;
  border-top: 1px solid rgba(17, 17, 16, 0.08);
}

.level-summary--top .level-summary__body {
  border-color: rgba(255, 255, 255, 0.1);
}

.level-summary__copy > span {
  display: block;
  margin-bottom: 5px;
  color: var(--ff-red);
  font-size: var(--ff-type-label-small);
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.level-summary--top .level-summary__copy > span {
  color: #f1cf6d;
}

.level-summary h3 {
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: clamp(1rem, 2vw, 1.3rem);
  letter-spacing: -0.04em;
  line-height: 1.1;
}

.level-summary p {
  margin: 0;
  color: #62625e;
  font-size: var(--ff-type-body-small);
  line-height: 1.4;
}

.level-summary--top p {
  color: rgba(255, 255, 255, 0.67);
}

.level-summary ul {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.level-summary li {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  min-height: 0;
  padding: 0 !important;
  border: 0 !important;
  color: #393936;
  font-size: 0.78rem;
  line-height: 1.3;
}

.level-summary--top li {
  color: rgba(255, 255, 255, 0.82);
}

.level-summary li .material-icons {
  color: var(--ff-red);
  font-size: 16px;
}

.level-summary--top li .material-icons {
  color: #f1cf6d;
}

.plans-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin: 28px 0 12px;
}

.plans-heading > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.plans-heading > div > span,
.plans-heading > span {
  color: var(--ff-gray-3);
  font-size: var(--ff-type-label-small);
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.plans-heading strong {
  font-family: "Montserrat", sans-serif;
  font-size: 1.25rem;
  letter-spacing: -0.035em;
}

.plan-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.plan-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 205px;
  padding: 22px;
  border: 1px solid rgba(17, 17, 16, 0.09);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 14px 34px rgba(17, 17, 16, 0.065), inset 0 1px 0 rgba(255, 255, 255, 0.96);
}

.plan-card__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.plan-card__heading span {
  color: #c8102e;
  font-size: 0.6875rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.plan-card__heading strong {
  color: #6d6d68;
  font-size: 0.72rem;
}

.plan-card__price {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 30px 0 22px;
}

.plan-card__price strong {
  font-family: "Montserrat", sans-serif;
  font-size: clamp(1.25rem, 2.4vw, 1.75rem);
  letter-spacing: -0.045em;
  line-height: 1.08;
}

.plan-card__price span {
  color: #676762;
  font-size: 0.75rem;
  font-weight: 700;
}

.plan-card__purchase {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  margin-top: auto;
  margin-left: auto;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #c8102e;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 9px 20px rgba(200, 16, 46, 0.2);
}

.plan-card__purchase .material-icons {
  color: #fff !important;
  font-size: 21px;
}

.plan-card__purchase:focus-visible {
  outline: 3px solid rgba(200, 16, 46, 0.28);
  outline-offset: 3px;
}

.plan-card__details {
  margin-top: 10px;
  border-top: 1px solid rgba(17, 17, 16, 0.09);
}

.plan-card__details summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 44px;
  color: #4f4f4b;
  cursor: pointer;
  font-size: var(--ff-type-label-small);
  font-weight: 900;
  letter-spacing: 0.08em;
  list-style: none;
  text-transform: uppercase;
}

.plan-card__details summary::-webkit-details-marker {
  display: none;
}

.plan-card__details summary .material-icons {
  color: var(--ff-red);
  font-size: 19px;
  transition: transform 0.2s ease;
}

.plan-card__details[open] summary .material-icons {
  transform: rotate(180deg);
}

.plan-card__details ul {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 0;
  padding: 0 0 4px;
  list-style: none;
}

.plan-card__details li {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 7px;
  min-height: 0;
  padding: 9px !important;
  border: 0 !important;
  border-radius: 11px;
  background: rgba(200, 16, 46, 0.055);
  color: #4d4d49;
  font-size: 0.7rem;
  line-height: 1.25;
}

.plan-card__details li strong {
  color: var(--ff-red);
  font-family: "Montserrat", sans-serif;
  font-size: 1rem;
  line-height: 1;
}

.pt-groups {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.pt-group {
  padding: 22px;
  border: 1px solid rgba(17, 17, 16, 0.09);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 14px 38px rgba(17, 17, 16, 0.06);
}

.pt-group__heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.pt-group__heading > .material-icons {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(200, 16, 46, 0.09);
  color: #c8102e;
  font-size: 22px;
}

.pt-group__heading span:not(.material-icons) {
  color: #8a8a85;
  font-size: 0.625rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.pt-group__heading h3 {
  margin: 3px 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 1.2rem;
}

.pt-plan-list {
  display: grid;
  gap: 8px;
}

.pt-plan {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto auto;
  gap: 7px 12px;
  min-width: 0;
  min-height: 0;
  padding: 15px;
  border: 1px solid rgba(17, 17, 16, 0.09);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 12px 28px rgba(17, 17, 16, 0.055), inset 0 1px 0 rgba(255, 255, 255, 0.96);
}

.pt-plan__heading {
  display: flex;
  grid-column: 1;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.pt-plan__heading span {
  color: var(--ff-red);
  font-size: 0.6875rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.pt-plan__heading strong {
  color: #6d6d68;
  font-size: 0.72rem;
}

.pt-plan__price {
  display: flex;
  grid-column: 1;
  flex-direction: column;
  gap: 2px;
}

.pt-plan__price strong {
  font-family: "Montserrat", sans-serif;
  font-size: 1.45rem;
  letter-spacing: -0.045em;
  line-height: 1.08;
}

.pt-plan__price span {
  color: #676762;
  font-size: 0.75rem;
  font-weight: 700;
}

.pt-plan__purchase {
  display: grid;
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: center;
  place-items: center;
  width: 54px;
  height: 54px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--ff-red);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 9px 20px rgba(200, 16, 46, 0.2);
}

.pt-plan__purchase .material-icons {
  color: #fff !important;
  font-size: 21px;
}

.pt-plan__purchase:focus-visible {
  outline: 3px solid rgba(200, 16, 46, 0.28);
  outline-offset: 3px;
}

.maintenance-section {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
  gap: clamp(24px, 5vw, 56px);
  margin-top: clamp(46px, 6vw, 72px);
  padding: clamp(24px, 4vw, 38px);
  border: 1px solid rgba(191, 148, 44, 0.48);
  border-radius: 24px;
  background:
    radial-gradient(circle at 100% 0, rgba(220, 174, 55, 0.2), transparent 38%),
    linear-gradient(145deg, #1c1a14, #11110f);
  color: #fff;
  box-shadow: 0 22px 54px rgba(44, 34, 12, 0.16);
  scroll-margin-top: 80px;
}

.maintenance-section__intro {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.maintenance-section__intro > div {
  min-width: 0;
}

.maintenance-section__icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 50px;
  height: 50px;
  border: 1px solid rgba(242, 207, 107, 0.4);
  border-radius: 16px;
  background: rgba(242, 207, 107, 0.08);
  color: #f2cf6b;
  font-size: 24px;
}

.maintenance-section__eyebrow {
  display: block;
  margin-bottom: 7px;
  color: #f2cf6b;
  font-size: var(--ff-type-label-small);
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.maintenance-section h2 {
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: clamp(1.8rem, 4vw, 3rem);
  font-style: italic;
  font-weight: 900;
  letter-spacing: -0.05em;
  line-height: 0.98;
  text-transform: uppercase;
}

.maintenance-section__intro p {
  max-width: 640px;
  margin: 10px 0 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: var(--ff-type-body-small);
  line-height: 1.5;
}

.maintenance-section__rules {
  padding-left: clamp(0px, 3vw, 34px);
  border-left: 1px solid rgba(255, 255, 255, 0.11);
}

.maintenance-section__status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: 1px solid rgba(242, 207, 107, 0.28);
  border-radius: 999px;
  background: rgba(242, 207, 107, 0.08);
  color: #f2cf6b;
  font-size: var(--ff-type-label-small);
  font-weight: 800;
  text-transform: uppercase;
}

.maintenance-section__status .material-icons {
  color: #fff !important;
  font-size: 15px;
}

.maintenance-section__rules ul {
  display: grid;
  gap: 7px;
  margin: 15px 0 17px;
  padding: 0;
  list-style: none;
}

.maintenance-section__rules li {
  position: relative;
  min-height: 0;
  padding: 0 0 0 18px !important;
  border: 0 !important;
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.8rem;
  line-height: 1.35;
}

.maintenance-section__rules li::before {
  content: "";
  position: absolute;
  top: 0.5em;
  left: 1px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f2cf6b;
}

.maintenance-section__rules a {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 44px;
  padding: 10px 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f2d477, #d5a72f);
  color: #17140b;
  font-size: var(--ff-type-label);
  font-weight: 900;
  letter-spacing: 0.06em;
  text-decoration: none;
  text-transform: uppercase;
}

.maintenance-section__rules a .material-icons {
  font-size: 18px;
}

.services-help {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 28px;
  margin-top: 14px;
  padding: clamp(24px, 5vw, 42px);
  border-radius: 28px;
  background: #111110;
  color: #fff;
}

.services-help p {
  max-width: 680px;
  color: rgba(255, 255, 255, 0.64);
}

.services-help a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 48px;
  padding: 12px 18px;
  border: 1px solid rgba(100, 220, 142, 0.45);
  border-radius: 999px;
  background: rgba(37, 211, 102, 0.11);
  color: #aaf0c2;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
}

.services-help a .material-icons {
  color: #fff !important;
  font-size: 19px;
}

@media (max-width: 800px) {
  .services-shell {
    padding: 18px 12px calc(76px + max(8px, env(safe-area-inset-bottom, 0px)));
  }

  .services-hero {
    grid-template-columns: 1fr;
    min-height: 0;
    padding: 24px 20px;
    border-radius: 18px;
  }

  .services-hero__mark {
    display: none;
  }

  .services-hero h1 {
    max-width: 10ch;
    font-size: clamp(2.15rem, 10vw, 3rem);
    line-height: 0.94;
  }

  .services-hero__copy > p {
    font-size: var(--ff-type-body-small);
  }

  .services-nav {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 5px;
    padding: 6px;
  }

  .services-nav button {
    flex-direction: column;
    gap: 3px;
    min-height: 54px;
    padding: 6px 3px;
    font-size: 0.62rem;
    line-height: 1.1;
    text-align: center;
  }

  .pt-groups {
    grid-template-columns: 1fr;
  }

  .section-heading--row {
    display: block;
  }

  .section-heading h2,
  .services-help h2 {
    font-size: clamp(1.55rem, 7vw, 2rem);
    line-height: 1;
  }

  .level-switch {
    margin-top: 18px;
  }

  .level-switch--services {
    margin-top: 0;
  }

  .level-summary__body {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .maintenance-section {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .maintenance-section__rules {
    padding: 20px 0 0;
    border-top: 1px solid rgba(255, 255, 255, 0.11);
    border-left: 0;
  }

  .plan-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .services-help {
    grid-template-columns: 1fr;
  }

  .services-help a {
    width: 100%;
    box-sizing: border-box;
  }
}

@media (max-width: 480px) {
  .services-hero h1 {
    font-size: clamp(2.05rem, 11vw, 3rem);
  }

  .services-section {
    padding-top: 42px;
  }

  .services-section--coaching {
    padding-top: 34px;
  }

  .section-heading {
    margin-bottom: 14px;
  }

  .section-heading > span,
  .section-heading > div > span {
    margin-bottom: 7px;
  }

  .level-switch--services {
    gap: 5px;
    padding: 5px;
    border-radius: 17px;
  }

  .level-switch--services button {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 7px;
    min-height: 92px;
    padding: 9px;
    border-radius: 13px;
  }

  .level-switch__check {
    font-size: 18px;
  }

  .level-switch__copy strong {
    font-size: 0.95rem;
  }

  .level-switch__copy small {
    font-size: 0.6rem;
  }

  .level-switch__price {
    grid-column: 2;
    font-size: 0.65rem;
  }

  .level-summary {
    border-radius: 17px;
  }

  .level-summary summary {
    padding: 11px 13px;
  }

  .level-summary__body {
    padding: 13px;
  }

  .level-summary__toggle {
    font-size: 0.62rem;
  }

  .level-summary ul {
    gap: 7px 9px;
  }

  .level-summary li {
    font-size: 0.72rem;
  }

  .plans-heading {
    margin-top: 22px;
  }

  .plan-grid {
    grid-template-columns: 1fr;
  }

  .plan-card {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-rows: auto auto;
    gap: 7px 12px;
    min-height: 0;
    padding: 15px;
    border-radius: 17px;
  }

  .plan-card__heading {
    grid-column: 1;
  }

  .plan-card__price {
    grid-column: 1;
    gap: 2px;
    margin: 0;
  }

  .plan-card__price strong {
    font-size: 1.22rem;
  }

  .plan-card__purchase {
    grid-column: 2;
    grid-row: 1 / span 2;
    align-self: center;
    width: 54px;
    height: 54px;
    margin: 0;
  }

  .plan-card__details {
    grid-column: 1 / -1;
    grid-row: 3;
    margin-top: 2px;
  }

  .plan-card__details ul {
    gap: 6px;
  }

  .plan-card__details li {
    padding: 8px !important;
    font-size: 0.68rem;
  }

  .pt-group {
    padding: 17px;
  }

  .maintenance-section {
    padding: 20px 17px;
    border-radius: 19px;
  }

  .maintenance-section__intro {
    gap: 12px;
  }

  .maintenance-section__icon {
    width: 43px;
    height: 43px;
    border-radius: 13px;
    font-size: 21px;
  }

  .maintenance-section h2 {
    font-size: 1.45rem;
    letter-spacing: -0.06em;
  }

  .maintenance-section__rules a {
    width: 100%;
    box-sizing: border-box;
  }

  .services-help {
    padding: 21px 17px;
    border-radius: 19px;
  }
}
</style>
